import { Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Skeleton } from "@/components/ui/skeleton";
import { useHcbbRecentSearches } from "@/hooks/use-hcbb-cost-estimator";
import type { HcbbRecentSearch } from "@/model/hcbb-cost-estimator";

/** Rows drawn while the list loads — the panel keeps its shape. */
const SKELETON_ROW_COUNT = 3;

/**
 * The searches this member has run before, as a way back into one of them.
 *
 * An empty list — no history yet, or the request failed — is told to the
 * member plainly rather than filled with rows they never searched; nothing
 * here is fabricated to make the panel look busier than it is.
 */
export const HcbbRecentSearches = () => {
  const navigate = useNavigate();
  const { searches, isPending, rerunSearch } = useHcbbRecentSearches();

  return (
    <div>
      <h2 className="mb-2 font-medium">Recent Searches</h2>

      {isPending ? (
        <RecentSearchesSkeleton />
      ) : searches.length === 0 ? (
        <NoRecentSearches />
      ) : (
        <div className="space-y-4">
          {searches.slice(0, 3).map((search) => (
            <RecentSearchRow
              key={search.id}
              search={search}
              onSelect={() => navigate(rerunSearch(search))}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * One row, as a control back into the search it came from.
 *
 * `displayName` is the procedure's own dropdown label, and `address` is the
 * location typeahead's text — both fall back to a bare id/zip in the service
 * when the endpoint sends none, so there is always something readable here.
 */
const RecentSearchRow = ({
  search,
  onSelect,
}: {
  search: HcbbRecentSearch;
  onSelect: () => void;
}) => (
  <button
    type="button"
    onClick={onSelect}
    aria-label={`Search again for ${search.displayName} near ${search.address}`}
    className="border-1 flex w-full cursor-pointer items-center gap-2 rounded-lg border px-4 py-3 text-left shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
  >
    <Clock aria-hidden="true" className="shrink-0 text-primary" />
    <div className="min-w-0">
      <h3 className="truncate text-sm font-medium">{search.displayName}</h3>
      <p className="truncate text-sm text-gray-600">{search.address}</p>
    </div>
  </button>
);

/**
 * Nothing to show yet, said plainly — and pointed at the form above, which is
 * the only thing that can fill this panel.
 */
const NoRecentSearches = () => (
  <div className="border-1 border-gray flex items-center gap-3 rounded-lg border p-4 text-sm shadow-md">
    <Clock aria-hidden="true" className="shrink-0 text-gray-400" />
    <div>
      <p className="font-medium text-gray-800">No recent searches yet</p>
      <p className="text-gray-600">
        You can start your first search by filling the search form above.
      </p>
    </div>
  </div>
);

const RecentSearchesSkeleton = () => (
  <div aria-hidden="true" className="space-y-4">
    {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
      <Skeleton
        key={index}
        className="h-[70px] w-full rounded-lg bg-gray-200"
      />
    ))}
  </div>
);
